import { useMemo, useState } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { APCMStateBadge } from "./APCMStateBadge"
import { cn } from "@/lib/utils"
import { Search, X, Filter, ChevronDown, User, ArrowRightCircle } from "lucide-react"
import { STATE_PHASES, PHASE_LABELS, APCMState } from "../types/apcm-ops.types"
import type { StatePhase } from "../types/apcm-ops.types"
import type { PatientCurrentState } from "../types/apcm-ops.types"
import type { PatientResponse } from "@/lib/types"
import { parseApiTimestamp } from "@/lib/datetime"

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return "-"
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
  return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`
}

function durationColor(seconds: number | null): string {
  if (seconds === null) return "text-muted-foreground"
  if (seconds < 3600) return "text-success"
  if (seconds < 86400) return "text-muted-foreground"
  if (seconds < 604800) return "text-warning"
  return "text-warning font-medium"
}

function formatTimestamp(iso: string): string {
  const d = parseApiTimestamp(iso)
  if (!d) return iso
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

interface PatientStateTableProps {
  states: PatientCurrentState[]
  patients: PatientResponse[]
  stateFilter: string | null
  searchQuery: string
  selectedIds: Record<string, true>
  onStateFilterChange: (state: string | null) => void
  onSearchChange: (query: string) => void
  onToggleSelect: (id: string) => void
  onSelectAll: (ids: string[]) => void
  onClearSelection: () => void
  onRowClick: (patientId: string) => void
  onBulkTransition?: (patientIds: string[], newState: string) => Promise<void>
}

export function PatientStateTable({
  states,
  patients,
  stateFilter,
  searchQuery,
  selectedIds,
  onStateFilterChange,
  onSearchChange,
  onToggleSelect,
  onSelectAll,
  onClearSelection,
  onRowClick,
  onBulkTransition,
}: PatientStateTableProps) {
  const [filterOpen, setFilterOpen] = useState(false)
  const [moveToStateOpen, setMoveToStateOpen] = useState(false)
  const [confirmState, setConfirmState] = useState<string | null>(null)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const selectedStates = useMemo(() => {
    if (!stateFilter) return new Set<string>()
    return new Set(stateFilter.split(","))
  }, [stateFilter])

  const patientLookup = useMemo(() => {
    const map = new Map<string, PatientResponse>()
    for (const p of patients) {
      map.set(p.id, p)
    }
    return map
  }, [patients])

  const rows = useMemo(() => {
    let filtered = states

    if (stateFilter) {
      const filterStates = stateFilter.split(",")
      filtered = filtered.filter((s) => filterStates.includes(s.current_state))
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      filtered = filtered.filter((s) => {
        const p = patientLookup.get(s.patient_id)
        const name = s.patient_name
          ? s.patient_name.toLowerCase()
          : p
            ? `${p.first_name} ${p.last_name}`.toLowerCase()
            : s.patient_id.toLowerCase()
        return name.includes(q) || s.current_state.toLowerCase().includes(q)
      })
    }

    return filtered.map((s) => {
      const p = patientLookup.get(s.patient_id)
      const name = s.patient_name || (p ? `${p.first_name} ${p.last_name}` : null)
      const phone = s.phone_number || p?.phone_number
      return {
        ...s,
        patient_name: name,
        patient_initials: name
          ? `${name.split(" ")[0][0]}${name.split(" ").slice(-1)[0][0]}`
          : null,
        phone_number: phone,
      }
    })
  }, [states, stateFilter, searchQuery, patientLookup])

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds[r.patient_id])

  // State filter helpers
  const toggleState = (state: string) => {
    const next = new Set(selectedStates)
    if (next.has(state)) {
      next.delete(state)
    } else {
      next.add(state)
    }
    onStateFilterChange(next.size > 0 ? Array.from(next).join(",") : null)
  }

  const togglePhase = (phase: StatePhase) => {
    const phaseStates = STATE_PHASES[phase]
    const allSelected = phaseStates.every((s) => selectedStates.has(s))
    const next = new Set(selectedStates)
    if (allSelected) {
      phaseStates.forEach((s) => next.delete(s))
    } else {
      phaseStates.forEach((s) => next.add(s))
    }
    onStateFilterChange(next.size > 0 ? Array.from(next).join(",") : null)
  }

  const clearAllFilters = () => {
    onStateFilterChange(null)
  }

  return (
    <div className="space-y-3">
      {/* Filters bar */}
      <div className="flex flex-wrap items-center gap-2 md:gap-3">
        <div className="relative w-full sm:w-auto sm:flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search by name or state..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>

        {/* State filter dropdown */}
        <Popover open={filterOpen} onOpenChange={setFilterOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "h-9 gap-1.5 text-sm",
                selectedStates.size > 0 && "border-primary/30 bg-primary/5 text-primary"
              )}
            >
              <Filter className="h-3.5 w-3.5" />
              States{selectedStates.size > 0 && ` (${selectedStates.size})`}
              <ChevronDown className="h-3 w-3 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start">
            <div className="max-h-80 overflow-y-auto p-2 space-y-1">
              {(Object.keys(STATE_PHASES) as StatePhase[]).map((phase) => {
                const phaseStates = STATE_PHASES[phase]
                const allPhaseSelected = phaseStates.every((s) => selectedStates.has(s))
                const somePhaseSelected = phaseStates.some((s) => selectedStates.has(s))
                return (
                  <div key={phase}>
                    <div
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer"
                      onClick={() => togglePhase(phase)}
                    >
                      <Checkbox
                        checked={allPhaseSelected ? true : somePhaseSelected ? "indeterminate" : false}
                        onCheckedChange={() => togglePhase(phase)}
                        className="pointer-events-none"
                      />
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {PHASE_LABELS[phase]}
                      </span>
                    </div>
                    <div className="ml-4 space-y-0.5">
                      {phaseStates.map((state) => (
                        <div
                          key={state}
                          className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted/50 cursor-pointer"
                          onClick={() => toggleState(state)}
                        >
                          <Checkbox
                            checked={selectedStates.has(state)}
                            onCheckedChange={() => toggleState(state)}
                            className="pointer-events-none"
                          />
                          <APCMStateBadge state={state} />
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
            {selectedStates.size > 0 && (
              <div className="border-t p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs h-7"
                  onClick={clearAllFilters}
                >
                  Clear All
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>

        {stateFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onStateFilterChange(null)}
            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
        <span className="text-[11px] text-muted-foreground ml-auto tabular-nums">
          {rows.length} patient{rows.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Bulk action bar */}
      {Object.keys(selectedIds).length > 0 && (
        <div className="flex flex-wrap items-center gap-2 md:gap-3 rounded-lg border border-primary/20 bg-primary/5 p-2.5">
          <span className="text-xs font-medium text-primary">
            {Object.keys(selectedIds).length} selected
          </span>
          <Button variant="outline" size="sm" onClick={onClearSelection} className="h-7 text-xs">
            Clear
          </Button>

          {onBulkTransition && (
            <>
              <Popover open={moveToStateOpen} onOpenChange={setMoveToStateOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5">
                    <ArrowRightCircle className="h-3 w-3" />
                    Move to State
                    <ChevronDown className="h-3 w-3 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                  <div className="max-h-80 overflow-y-auto p-2 space-y-1">
                    {(Object.keys(STATE_PHASES) as StatePhase[]).map((phase) => (
                      <div key={phase}>
                        <div className="px-2 py-1.5">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {PHASE_LABELS[phase]}
                          </span>
                        </div>
                        <div className="space-y-0.5">
                          {STATE_PHASES[phase].map((state) => (
                            <button
                              key={state}
                              className="flex items-center gap-2 w-full px-2 py-1.5 rounded hover:bg-muted/50 text-left"
                              onClick={() => {
                                setMoveToStateOpen(false)
                                setConfirmState(state)
                              }}
                            >
                              <APCMStateBadge state={state} />
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Confirmation dialog */}
              {confirmState && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                  <div className="bg-card rounded-xl shadow-lg border p-6 max-w-sm w-full mx-4 space-y-4">
                    <h3 className="text-sm font-semibold">Confirm Bulk Transition</h3>
                    <p className="text-sm text-muted-foreground">
                      Move <span className="font-semibold text-foreground">{Object.keys(selectedIds).length}</span> patient{Object.keys(selectedIds).length !== 1 ? "s" : ""} to:
                    </p>
                    <div className="py-1">
                      <APCMStateBadge state={confirmState} />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmState(null)}
                        disabled={isTransitioning}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        disabled={isTransitioning}
                        onClick={async () => {
                          setIsTransitioning(true)
                          try {
                            await onBulkTransition(Object.keys(selectedIds), confirmState)
                          } finally {
                            setIsTransitioning(false)
                            setConfirmState(null)
                          }
                        }}
                      >
                        {isTransitioning ? "Transitioning..." : "Confirm"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border shadow-sm overflow-x-auto bg-card">
        <Table className="min-w-[760px]">
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      onSelectAll(rows.map((r) => r.patient_id))
                    } else {
                      onClearSelection()
                    }
                  }}
                />
              </TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider min-w-[200px]">Patient</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider">State</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">Time in State</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap">Last Transition</TableHead>
              <TableHead className="text-[11px] font-semibold uppercase tracking-wider">Workflow</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-12 text-sm bg-card">
                  No patients found
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.patient_id}
                  className={cn(
                    "cursor-pointer transition-colors bg-card hover:bg-muted/50 border-b",
                    selectedIds[row.patient_id] && "bg-primary/[0.03]"
                  )}
                  onClick={() => onRowClick(row.patient_id)}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds[row.patient_id]}
                      onCheckedChange={() => onToggleSelect(row.patient_id)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary text-[11px] font-semibold shrink-0">
                        {row.patient_initials ? (
                          row.patient_initials
                        ) : (
                          <User className="h-3.5 w-3.5" />
                        )}
                      </div>
                      <div className="min-w-0">
                        {row.patient_name ? (
                          <span className="font-medium text-[13px] block truncate">
                            {row.patient_name}
                          </span>
                        ) : (
                          <span className="text-[13px] text-muted-foreground italic block truncate">
                            Unknown Patient
                          </span>
                        )}
                        {row.phone_number && (
                          <span className="text-[11px] text-muted-foreground font-mono">
                            {row.phone_number}
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <APCMStateBadge state={row.current_state} />
                  </TableCell>
                  <TableCell>
                    <span className={cn("text-sm font-mono tabular-nums", durationColor(row.time_in_state_seconds))}>
                      {formatDuration(row.time_in_state_seconds)}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatTimestamp(row.last_transition)}
                  </TableCell>
                  <TableCell>
                    {row.workflow_id ? (
                      <span className="font-mono text-[11px] text-muted-foreground bg-muted/50 rounded px-1.5 py-0.5">
                        {row.workflow_id.slice(0, 8)}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground/50">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
